import React, { useCallback, useMemo, useEffect } from "react"
import { Box, Typography, Divider } from "@mui/material"
import { ContactMethod, ContactPreferencesStepProps } from "./types"
import { COPY, SUPPORT_HOURS_START, SUPPORT_HOURS_END } from "./constants"
import ContactMethodCard from "./ContactMethodCard"
import ContactInfoForm from "./ContactInfoForm"
import { REGEX } from "../../constants"
import { formatTimeRange, isWeekend } from "./utils"

const ContactPreferencesStep: React.FC<ContactPreferencesStepProps> = ({
  existingPhoneNumber,
  existingEmail,
  contactPreferences,
  setContactPreferences,
  contactInfo,
  setContactInfo,
  setIsContactInfoValid,
  isContactInfoValid,
  resetValidationError,
  showContactInfoForm,
  setShowContactInfoForm,
  handleContactInfoChange,
}) => {
  const contactMethods = useMemo(
    () => [
      {
        method: ContactMethod.PHONE,
        copy: COPY.CONTACT_PREFERENCES.PHONE,
        isRecommended: true,
        hours: isWeekend()
          ? undefined
          : `${COPY.CONTACT_PREFERENCES.PHONE.HOURS} ${formatTimeRange(
              SUPPORT_HOURS_START,
              SUPPORT_HOURS_END
            )}`,
      },
      {
        method: ContactMethod.EMAIL,
        copy: COPY.CONTACT_PREFERENCES.EMAIL,
        isRecommended: false,
        hours: undefined,
      },
    ],
    []
  )

  const handleContactMethodSelect = useCallback(
    (method: ContactMethod) => {
      if (contactPreferences === method) return
      setContactPreferences(method)
      setShowContactInfoForm(false)
      setContactInfo("")
      resetValidationError()
      setIsContactInfoValid(null)
    },
    [
      contactPreferences,
      setContactPreferences,
      setContactInfo,
      resetValidationError,
      setIsContactInfoValid,
      setShowContactInfoForm,
    ]
  )

  const handleRequestButtonClick = useCallback(() => {
    setShowContactInfoForm(true)
    setIsContactInfoValid(null)
  }, [setIsContactInfoValid, setShowContactInfoForm])

  useEffect(() => {
    if (!contactInfo.trim() || !showContactInfoForm) return
    const isValid =
      contactPreferences === ContactMethod.PHONE
        ? REGEX.phone.test(contactInfo)
        : REGEX.email.test(contactInfo)
    setIsContactInfoValid(isValid)
  }, [contactInfo, contactPreferences, showContactInfoForm, setIsContactInfoValid])

  return (
    <>
      <Typography variant="h5" gutterBottom sx={{ opacity: showContactInfoForm ? 0.6 : 1 }}>
        {COPY.CONTACT_PREFERENCES.TITLE}
      </Typography>
      <Typography
        variant="body1"
        gutterBottom
        color="text.secondary"
        sx={{ opacity: showContactInfoForm ? 0.6 : 1 }}
      >
        {COPY.CONTACT_PREFERENCES.SUBTITLE}
      </Typography>

      <Box sx={{ mt: 2, mb: 3 }}>
        {contactMethods.map(
          ({ method, copy, isRecommended, hours }) =>
            (!showContactInfoForm || contactPreferences === method) && (
              <ContactMethodCard
                key={method}
                method={method}
                title={copy.TITLE}
                description={copy.DESCRIPTION}
                buttonText={copy.BUTTON}
                isSelected={contactPreferences === method}
                isRecommended={isRecommended}
                showContactInfoForm={showContactInfoForm}
                hours={hours}
                onSelect={handleContactMethodSelect}
                onRequest={handleRequestButtonClick}
              />
            )
        )}
      </Box>

      {showContactInfoForm && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 3 }} />
          {contactPreferences === ContactMethod.PHONE ? (
            <ContactInfoForm
              title={COPY.CONTACT_PREFERENCES.PHONE.CONFIRM_TITLE}
              existingInfo={existingPhoneNumber}
              existingTemplate={COPY.CONTACT_PREFERENCES.PHONE.EXISTING_PHONE_NUMBER}
              missingMessage={COPY.CONTACT_PREFERENCES.PHONE.MISSING_PHONE_NUMBER}
              label="Phone number"
              error={isContactInfoValid === false}
              errorMessage={COPY.CONTACT_PREFERENCES.PHONE.ERROR}
              value={contactInfo}
              onChange={handleContactInfoChange}
            />
          ) : (
            <ContactInfoForm
              title={COPY.CONTACT_PREFERENCES.EMAIL.CONFIRM_TITLE}
              existingInfo={existingEmail}
              existingTemplate={COPY.CONTACT_PREFERENCES.EMAIL.EXISTING_EMAIL}
              missingMessage={COPY.CONTACT_PREFERENCES.EMAIL.MISSING_EMAIL}
              label="Email address"
              error={isContactInfoValid === false}
              errorMessage={COPY.CONTACT_PREFERENCES.EMAIL.ERROR}
              value={contactInfo}
              onChange={handleContactInfoChange}
            />
          )}
        </Box>
      )}
    </>
  )
}

export default ContactPreferencesStep
